/**
 * Wave 4 §4.5 — CTA navigation map for Home promo `bannerTargetType`.
 * Prefer existing shop/org / offer buy-context handoffs; never invent unsupported deep-link query shapes.
 * Incomplete / unresolvable refs → null (FR-14 collapse — no non-actionable chrome).
 */

import type { PublicDiscoveryHomeBannerTargetType } from '../discovery/publicDiscoveryTypes.js';

export type BrowseHomePromoCtaAction =
  | { readonly kind: 'navigate'; readonly url: string }
  | { readonly kind: 'external'; readonly url: string }
  | { readonly kind: 'browseRoot' }
  | null;

function trimRef(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * Encode path segments for an in-app shop / offer buy-context URL.
 * Mirrors salesPoint / platform QR handoff (`/${tenant}/${sp}`).
 */
function navigateShopPathFromSegments(segments: readonly string[]): BrowseHomePromoCtaAction {
  const encoded = segments
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  if (encoded.length === 0) {
    return null;
  }
  return { kind: 'navigate', url: `/${encoded}` };
}

/**
 * Map banner target → navigation action.
 * `offer` → existing offer detail / buy-context (shop path handoff), never browseSearch.
 */
export function resolveBrowseHomePromoCta(input: {
  readonly targetType: PublicDiscoveryHomeBannerTargetType;
  readonly targetRef: string | null;
}): BrowseHomePromoCtaAction {
  const ref = trimRef(input.targetRef);

  switch (input.targetType) {
    case 'platform':
      return { kind: 'browseRoot' };

    case 'tenant': {
      if (ref === null) {
        return null;
      }
      if (ref.startsWith('/')) {
        return { kind: 'navigate', url: ref };
      }
      return { kind: 'navigate', url: `/${encodeURIComponent(ref)}/shops` };
    }

    case 'salesPoint': {
      if (ref === null) {
        return null;
      }
      // customerShopUrl-shaped (`/tenant/sp`) or tenant/sp without leading slash.
      if (ref.startsWith('/')) {
        return { kind: 'navigate', url: ref };
      }
      if (ref.includes('/')) {
        return navigateShopPathFromSegments(ref.split('/'));
      }
      // Bare salesPointId alone cannot build shop URL — open Obchody directory.
      return { kind: 'navigate', url: '/shops' };
    }

    case 'offer': {
      if (ref === null) {
        return null;
      }
      // Explicit in-app path (offer detail / shop buy-context already encoded).
      if (ref.startsWith('/')) {
        return { kind: 'navigate', url: ref };
      }
      // tenant/sp[/productId[/variantId]] → shop buy-context (platform QR offer peer).
      // Bare product name / id cannot open DiscoveryOfferDetailSheet without a DTO → null.
      if (ref.includes('/')) {
        const segments = ref.split('/').filter((segment) => segment.length > 0);
        if (segments.length >= 2) {
          // Shop handoff uses tenant + SP only (catalog has no ?productId= deep-link).
          return navigateShopPathFromSegments(segments.slice(0, 2));
        }
      }
      return null;
    }

    case 'url': {
      if (ref === null) {
        return null;
      }
      if (isHttpUrl(ref)) {
        return { kind: 'external', url: ref };
      }
      if (ref.startsWith('/')) {
        return { kind: 'navigate', url: ref };
      }
      return { kind: 'external', url: ref };
    }

    default:
      return null;
  }
}
